import { createApi } from "@reduxjs/toolkit/query/react";
import { AnyType, Webhook, WebhookSummary } from "@shared/interfaces";
import Methods from "@shared/constants/methods";
import { showProgress } from "@shared/utils";
import { axiosBaseQuery } from "@shared/utils/request";

import config from "../../../config";

export interface SaveWebhooksPayload {
  account_id: number;
  upsert: Webhook[];
  delete: number[];
}

export interface TestWebhookPayload {
  webhook: Webhook;
  data: object;
}

const initialData = {
  webhooks: [],
  summary: null,
};

export const webhooksApi = createApi({
  reducerPath: "webhooksApi",
  baseQuery: axiosBaseQuery({
    baseUrl: `${config.baseApiUrl}webhooks`,
  }),
  tagTypes: ["Webhooks", "WebhooksSummary"],
  endpoints: (build) => ({
    loadSummary: build.query<WebhookSummary, number>({
      providesTags: ["WebhooksSummary"],
      query: (account_id) => ({
        url: "/summary",
        params: { account_id },
      }),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        showProgress(dispatch, queryFulfilled);
      },
    }),
    loadList: build.query<Webhook[], number>({
      providesTags: ["Webhooks"],
      query: (account_id) => ({
        url: "",
        params: { account_id },
      }),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        showProgress(dispatch, queryFulfilled);
      },
    }),
    testWebhook: build.mutation<AnyType, TestWebhookPayload>({
      query: (data) => ({
        url: "/test",
        method: Methods.POST,
        body: data,
      }),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        showProgress(dispatch, queryFulfilled, {
          success: "Successfully reached target URL",
          error: "Failed to reach target URL",
        });
      },
    }),
    saveWebhooks: build.mutation<Webhook[], SaveWebhooksPayload>({
      query: (data: SaveWebhooksPayload) => ({
        url: "",
        method: Methods.PUT,
        body: data,
      }),
      invalidatesTags: ["WebhooksSummary"],
      onQueryStarted: (arg, { dispatch, queryFulfilled }) => {
        showProgress(dispatch, queryFulfilled);
        queryFulfilled.then((result) => {
          dispatch(webhooksApi.util.updateQueryData("loadList", arg.account_id, () => result.data));
        });
      },
    }),
  }),
});

export const useWebhookListQuery = (account_id?: number) => {
  const state = webhooksApi.endpoints.loadList.useQuery(account_id as number, {
    skip: !account_id,
  });

  return state.data ?? initialData.webhooks;
};

export const useWebhooksSummaryQuery = (account_id?: number) => {
  const state = webhooksApi.endpoints.loadSummary.useQuery(account_id as number, {
    skip: !account_id,
  });

  return state.data ?? initialData.summary;
};

export const useTestWebhookMutation = () => {
  const [mutate] = webhooksApi.endpoints.testWebhook.useMutation();

  return mutate;
};

export const useWebhooksSaveMutation = () => {
  const [mutate] = webhooksApi.endpoints.saveWebhooks.useMutation();

  return mutate;
};
